import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useCan } from '@/auth/AuthContext';
import { useUrlState } from '@/lib/useUrlState';
import { ApiError, api } from '@/lib/api';
import ClassEditModal from './ClassEditModal';

// Пастельная палитра для плашки класса (нейтральные тона). Стабильно по id.
const HUE_PALETTE = [155, 175, 140, 195, 130, 165, 185, 145, 110, 200];
const classHue = (id) => HUE_PALETTE[(Number(id) || 0) % HUE_PALETTE.length];

function classDisplayName(c) {
  if (c.name) return c.name;
  if (c.stage === 'primary' && c.number !== null && c.stream) {
    return `${c.number} «${c.stream}»`;
  }
  return c.code;
}

const URL_SCHEMA = { year: '', stage: '', q: '', view: 'cards', edit: '' };

export default function ClassesPage() {
  const navigate = useNavigate();
  const { tweaks, updateTweak } = useShell();
  const can = useCan();
  const canWrite  = can('classes', 'write');

  const [params, setParams] = useUrlState(URL_SCHEMA);

  const [years, setYears] = useState([]);
  const [rows, setRows]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Загрузка списка учебных годов и выбор активного по умолчанию.
  useEffect(() => {
    api.get('/api/years_study').then((res) => {
      const list = (res.rows || []).filter((y) => !y.archive);
      setYears(list);
      if (!params.year && list.length) {
        const today = new Date().toISOString().slice(0, 10);
        const cur = list.find((y) => y.dateStart <= today && today <= y.dateEnd) || list[0];
        setParams({ year: String(cur.id) });
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = () => {
    if (!params.year) return;
    setLoading(true); setError(null);
    const qs = new URLSearchParams();
    qs.set('year_id', params.year);
    if (params.stage) qs.set('stage', params.stage);
    api.get('/api/classes?' + qs.toString())
      .then((res) => setRows(res.rows || []))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [params.year, params.stage]);

  const filtered = useMemo(() => {
    const q = (params.q || '').trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((c) => {
      const hay = [c.code, c.name, classDisplayName(c), c.teacherName, c.assistantName, c.room]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [rows, params.q]);

  const totals = useMemo(() => {
    const kg = filtered.filter((c) => c.stage === 'kindergarten');
    const pr = filtered.filter((c) => c.stage === 'primary');
    return {
      classCount:    filtered.length,
      kindergarten:  kg.length,
      primary:       pr.length,
      students:      filtered.reduce((s, c) => s + (c.enrolled || 0), 0),
      // Разбивка зачисленных по ступеням — для подписи метрики.
      kgStudents:    kg.reduce((s, c) => s + (c.enrolled || 0), 0),
      prStudents:    pr.reduce((s, c) => s + (c.enrolled || 0), 0),
      capacity:      filtered.reduce((s, c) => s + (c.capacity || 0), 0),
      teachers:      new Set(filtered.map((c) => c.teacherId).filter(Boolean)).size,
    };
  }, [filtered]);

  const editing = useMemo(() => {
    if (!params.edit) return null;
    if (params.edit === 'new') return 'new';
    return rows.find((r) => r.id === parseInt(params.edit, 10)) || null;
  }, [params.edit, rows]);

  const yearSelected = years.find((y) => String(y.id) === String(params.year));

  return (
    <>
      <Topbar
        breadcrumb={['Обучение', 'Классы']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />

      <div className="cls-hero">
        <div className="cls-hero__title">
          <h1>Классы и группы</h1>
          {yearSelected && (
            <div className="cls-hero__year">
              {yearSelected.name} уч. год
            </div>
          )}
          <select
            className="cls-hero__yearSelect"
            value={params.year || ''}
            onChange={(e) => setParams({ year: e.target.value })}
          >
            {years.map((y) => <option key={y.id} value={y.id}>{y.name}</option>)}
          </select>
        </div>
        <div className="cls-hero__metrics">
          <Metric label="Всего классов" value={totals.classCount}
                  sub={`${totals.kindergarten} групп · ${totals.primary} классов`} />
          <Metric label="Учеников зачислено" value={totals.students}
                  sub={
                    `школа ${totals.prStudents} · сад ${totals.kgStudents}`
                    + (totals.capacity > 0
                      ? ` · ${Math.round(totals.students / totals.capacity * 100)}% от вместимости (${totals.capacity})`
                      : '')
                  } />
          <Metric label="Классных руководителей" value={totals.teachers}
                  sub="" />
          <Metric label="Свободных мест"
                  value={Math.max(0, totals.capacity - totals.students)}
                  sub={totals.capacity ? `из ${totals.capacity}` : ''} />
        </div>
      </div>

      <div className="cls-toolbar">
        <div className="cls-toolbar__search">
          <Icon name="search" size={13} />
          <input
            placeholder="Найти класс, группу или классного руководителя…"
            value={params.q || ''}
            onChange={(e) => setParams({ q: e.target.value })}
          />
          {params.q && (
            <button onClick={() => setParams({ q: '' })} title="Очистить">
              <Icon name="x" size={11} />
            </button>
          )}
        </div>
        <div className="cls-tabs">
          <button className={'cls-tab' + (!params.stage ? ' is-active' : '')}
                  onClick={() => setParams({ stage: '' })}>
            Все ступени <span className="cls-tab__count">{rows.length}</span>
          </button>
          <button className={'cls-tab' + (params.stage === 'kindergarten' ? ' is-active' : '')}
                  onClick={() => setParams({ stage: 'kindergarten' })}>
            Детский сад <span className="cls-tab__count">{rows.filter((r) => r.stage === 'kindergarten').length}</span>
          </button>
          <button className={'cls-tab' + (params.stage === 'primary' ? ' is-active' : '')}
                  onClick={() => setParams({ stage: 'primary' })}>
            Начальная школа <span className="cls-tab__count">{rows.filter((r) => r.stage === 'primary').length}</span>
          </button>
        </div>

        <div style={{ flex: 1 }} />

        <div className="cls-segmented">
          <button className={(params.view === 'table' ? '' : 'is-active')} onClick={() => setParams({ view: 'cards' })}>Карточки</button>
          <button className={(params.view === 'table' ? 'is-active' : '')} onClick={() => setParams({ view: 'table' })}>Таблица</button>
        </div>

        {canWrite && (
          <button className="btn btn--primary btn--sm" onClick={() => setParams({ edit: 'new' })}>
            <Icon name="plus" size={13} /> Создать класс
          </button>
        )}
      </div>

      <div className="cls-content">
        {error && <div style={{ color: 'var(--red-fg)', padding: 16 }}>Ошибка: {error}</div>}
        {loading ? (
          <div className="ref-empty">Загрузка…</div>
        ) : filtered.length === 0 ? (
          <div className="ref-empty">
            {params.q ? `Ничего не найдено по запросу «${params.q}»` : 'В этом году нет классов. Создайте первый.'}
          </div>
        ) : params.view === 'table' ? (
          <ClassesTable
            rows={filtered}
            onOpen={(c) => navigate('/classes/' + c.id)}
            onEdit={(c) => setParams({ edit: String(c.id) })}
            canWrite={canWrite}
          />
        ) : (
          <ClassesCards
            rows={filtered}
            onOpen={(c) => navigate('/classes/' + c.id)}
            onEdit={(c) => setParams({ edit: String(c.id) })}
            canWrite={canWrite}
            onAdd={() => setParams({ edit: 'new' })}
          />
        )}
      </div>

      {editing && (
        <ClassEditModal
          mode={editing === 'new' ? 'create' : 'edit'}
          cls={editing === 'new' ? null : editing}
          yearId={Number(params.year) || null}
          years={years}
          onClose={() => setParams({ edit: '' })}
          onSaved={(c) => {
            setRows((prev) => {
              const i = prev.findIndex((r) => r.id === c.id);
              if (i >= 0) { const n = prev.slice(); n[i] = c; return n; }
              return [...prev, c];
            });
          }}
          onDeleted={(id) => setRows((prev) => prev.filter((r) => r.id !== id))}
        />
      )}
    </>
  );
}

function Metric({ label, value, sub, tone }) {
  return (
    <div className={'cls-metric' + (tone ? ' cls-metric--' + tone : '')}>
      <div className="cls-metric__label">{label}</div>
      <div className="cls-metric__value">{value}</div>
      {sub && <div className="cls-metric__sub">{sub}</div>}
    </div>
  );
}

function ClassesCards({ rows, onOpen, onEdit, canWrite, onAdd }) {
  // Группируем по ступени, kindergarten сверху
  const byStage = { kindergarten: [], primary: [] };
  for (const c of rows) byStage[c.stage]?.push(c);
  const stages = [
    { id: 'kindergarten', title: 'Детский сад',     subtitle: 'Дошкольное отделение' },
    { id: 'primary',      title: 'Начальная школа', subtitle: '0–4 классы' },
  ];
  return (
    <div className="cls-stages">
      {stages.map((stage) => {
        const items = byStage[stage.id];
        if (!items || items.length === 0) return null;
        return (
          <section key={stage.id} className="cls-stage">
            <header className="cls-stage__head">
              <div>
                <h2 className="cls-stage__title">{stage.title}</h2>
                <p className="cls-stage__sub">{stage.subtitle}</p>
              </div>
              <div className="cls-stage__count">
                {items.length} {stage.id === 'kindergarten' ? 'групп' : 'классов'} · {items.reduce((s, c) => s + (c.enrolled || 0), 0)} учеников
              </div>
            </header>
            <div className="cls-grid">
              {items.map((c) => (
                <ClassCard
                  key={c.id}
                  cls={c}
                  onOpen={() => onOpen(c)}
                  onEdit={canWrite ? () => onEdit(c) : null}
                />
              ))}
              {canWrite && (
                <button className="cls-card cls-card--add" onClick={onAdd}>
                  <div className="cls-card--add__inner">
                    <Icon name="plus" size={18} />
                    <span>{stage.id === 'kindergarten' ? 'Новая группа' : 'Новый класс'}</span>
                  </div>
                </button>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function ClassCard({ cls, onOpen, onEdit }) {
  const hue = classHue(cls.id);
  const tone = `oklch(0.54 0.12 ${hue})`;
  const toneSoft = `oklch(0.96 0.04 ${hue})`;
  const toneSoft2 = `oklch(0.88 0.06 ${hue})`;
  const cap = cls.capacity || 0;
  const fillPct = cap > 0 ? Math.round((cls.enrolled || 0) / cap * 100) : 0;
  const free = Math.max(0, cap - (cls.enrolled || 0));
  return (
    <div className="cls-card" onClick={onOpen} style={{ cursor: 'pointer' }}>
      <div className="cls-card__head">
        <div className="cls-card__badge" style={{ background: toneSoft, color: tone, borderColor: toneSoft2 }}>
          <span className="cls-card__code">{cls.code}</span>
        </div>
        <div className="cls-card__title">
          <div className="cls-card__name">{classDisplayName(cls)}</div>
          <div className="cls-card__meta">
            {cls.ageRange ? cls.ageRange : '—'}
            {cls.room && <> · {cls.room}</>}
          </div>
        </div>
        {onEdit && (
          <button
            type="button"
            className="cls-card__editBtn"
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            title="Редактировать"
          >
            <Icon name="edit" size={13} />
          </button>
        )}
      </div>

      <div className="cls-card__teacher">
        <div className="cls-card__teacherAva" style={{ background: toneSoft, color: tone }}>
          {initials(cls.teacherName)}
        </div>
        <div className="cls-card__teacherText">
          <div className="cls-card__teacherName">{cls.teacherName || '— классный руководитель не назначен —'}</div>
          <div className="cls-card__teacherRole">Классный руководитель</div>
        </div>
      </div>

      <div className="cls-card__capacity">
        <div className="cls-card__capacityHead">
          <span className="cls-card__capacityLabel">Наполняемость</span>
          <span className="cls-card__capacityValue">
            <b>{cls.enrolled || 0}</b><span className="muted">/{cap || '—'}</span>
            {cap > 0 && free > 0 && <span className="cls-card__free">· {free} свободно</span>}
            {cap > 0 && free === 0 && <span className="cls-card__free cls-card__free--full">· укомплектовано</span>}
          </span>
        </div>
        {cap > 0 && (
          <div className="cls-bar">
            <div className="cls-bar__fill" style={{ width: fillPct + '%', background: tone }} />
          </div>
        )}
      </div>

      {cls.assistantName && (
        <div className="cls-card__assistant">
          <Icon name="users" size={11} />
          <span>{cls.stage === 'kindergarten' ? 'Помощник' : 'Ассистент'}: {cls.assistantName}</span>
        </div>
      )}
    </div>
  );
}

function ClassesTable({ rows, onOpen, onEdit, canWrite }) {
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th style={{ width: 80 }}>Код</th>
            <th>Название</th>
            <th style={{ width: 100 }}>Ступень</th>
            <th style={{ width: 220 }}>Классный руководитель</th>
            <th style={{ width: 130 }}>Кабинет</th>
            <th style={{ width: 110 }}>Возраст</th>
            <th style={{ width: 160 }}>Наполняемость</th>
            <th style={{ width: 60 }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => {
            const hue = classHue(c.id);
            const cap = c.capacity || 0;
            const fill = cap > 0 ? Math.round((c.enrolled || 0) / cap * 100) : 0;
            return (
              <tr key={c.id} onClick={() => onOpen(c)} style={{ cursor: 'pointer' }}>
                <td>
                  <span className="cls-codepill" style={{
                    background: `oklch(0.96 0.04 ${hue})`,
                    color: `oklch(0.46 0.12 ${hue})`,
                    borderColor: `oklch(0.88 0.06 ${hue})`,
                  }}>
                    {c.code}
                  </span>
                </td>
                <td><span style={{ fontWeight: 500 }}>{classDisplayName(c)}</span></td>
                <td className="muted">{c.stage === 'kindergarten' ? 'Детский сад' : 'Школа'}</td>
                <td className="muted">{c.teacherName || <span style={{ color: 'var(--fg-faint)' }}>—</span>}</td>
                <td className="muted">{c.room || <span style={{ color: 'var(--fg-faint)' }}>—</span>}</td>
                <td className="muted">{c.ageRange || <span style={{ color: 'var(--fg-faint)' }}>—</span>}</td>
                <td>
                  {cap > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div style={{ fontSize: 11.5 }}><b>{c.enrolled || 0}</b><span className="muted">/{cap}</span> · {fill}%</div>
                      <div className="cls-bar cls-bar--sm">
                        <div className="cls-bar__fill" style={{ width: fill + '%', background: `oklch(0.54 0.12 ${hue})` }} />
                      </div>
                    </div>
                  ) : <span className="muted mono">{c.enrolled || 0}</span>}
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  {canWrite && (
                    <div className="ref-row-actions">
                      <button onClick={() => onEdit(c)} title="Редактировать">
                        <Icon name="edit" size={14} />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function initials(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase();
}
